import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Select, Spin, Switch, Tag, Typography } from 'antd';
import axios from 'axios';
import { API_DOMAIN } from '../constants';
import { useAccess } from '../../features/access/context/AccessProvider';
import { mailError, mailRequest } from './mailboxModel';
import { emailDocument } from './emailRendering';
import { signatureSaveBody, signatureText } from './signatureModel';
import EmailHtmlViewer from './EmailHtmlViewer';
import MailEditor from './MailEditor';
import './signature.css';

export default function SignatureSettings({ userId }) {
  const { can } = useAccess();
  const allowed = can(userId ? 'users.workspace_mailbox.settings.manage' : 'mailbox.view');
  const editable = can(userId ? 'users.workspace_mailbox.settings.manage' : 'mailbox.settings.manage');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [source, setSource] = useState(false);
  const [conflictDraft, setConflictDraft] = useState(null);
  const generation = useRef(0);
  const request = useCallback(async options => {
    if (!userId) return mailRequest('/settings/signature', options);
    const response = await axios({ url: `${API_DOMAIN}/auth/users/${encodeURIComponent(userId)}/workspace-mailbox/signature`, method: 'get', ...options, headers: { accessToken: localStorage.getItem('accessToken') } });
    if (!response.data?.success) throw new Error(response.data?.message || 'Unable to load signature.');
    return response.data.data;
  }, [userId]);
  const apply = data => {
    const saved = data.signature;
    setCurrent(saved); setIdentities(data.send_as_identities || []);
    setForm(saved || { send_as_email: data.send_as_identities?.find(row => row.is_primary)?.send_as_email || '', signature_html: '', enabled: true, apply_to_new_messages: true, apply_to_replies: true, sync_to_gmail: true });
  };
  const load = useCallback(async () => {
    if (!allowed) return;
    const ticket = ++generation.current;
    setLoading(true); setError('');
    try { const data = await request(); if (ticket === generation.current) apply(data); }
    catch (failure) { if (ticket === generation.current) setError(mailError(failure)); }
    finally { if (ticket === generation.current) setLoading(false); }
  }, [allowed, request]);
  useEffect(() => {
    const requests = generation;
    load();
    return () => { requests.current++; };
  }, [load]);
  const save = async () => {
    if (!editable || saving) return;
    if (new Blob([form.signature_html]).size > 100000) { setError('Signature HTML must be no more than 100 KB.'); return; }
    const ticket = generation.current;
    setSaving(true); setError(''); setNotice('');
    try {
      const saved = await request({ method: 'put', data: signatureSaveBody(form, current) });
      if (ticket !== generation.current) return;
      setCurrent(saved); setForm(saved); setConflictDraft(null);
      setNotice('Signature saved in SRM.');
    } catch (failure) {
      if (ticket !== generation.current) return;
      if (failure.response?.status === 409 && failure.response?.data?.data?.code === 'MAIL_SIGNATURE_VERSION_CONFLICT') {
        setConflictDraft(form);
        setNotice('Someone updated this signature. The latest setting is loaded below. Review it before saving again. Your attempted HTML is retained below for reference.');
        setForm(null);
        await load();
      } else setError(mailError(failure));
    } finally { setSaving(false); }
  };
  if (!allowed) return null;
  if (loading) return <Spin aria-label="Loading signature" />;
  const change = values => setForm(previous => ({ ...previous, ...values }));
  return <div className="mail-signature-settings">
    {error && <Alert type="error" message={error} action={!form && <Button onClick={load}>Retry</Button>} />}
    {notice && <Alert type={conflictDraft ? 'warning' : 'success'} message={notice} />}
    {conflictDraft && <details><summary>Your attempted signature HTML</summary><Input.TextArea readOnly value={conflictDraft.signature_html} rows={4} /></details>}
    {form && <>
      <Typography.Paragraph type="secondary">Create your SRM signature here. An existing Gmail signature cannot be imported. Syncing replaces the signature for the selected Gmail identity.</Typography.Paragraph>
      {!editable && <Alert type="info" message="You can view this signature. Editing requires mailbox settings permission." />}
      <label>Gmail identity<Select aria-label="Signature Gmail identity" disabled={!editable || saving} value={form.send_as_email || undefined} onChange={send_as_email => change({ send_as_email })} options={identities.map(row => ({ value: row.send_as_email, label: row.send_as_email + (row.is_primary ? ' (primary)' : ''), disabled: !row.is_primary && row.verification_status !== 'accepted' }))} /></label>
      <Typography.Text type="secondary">The SRM composer sends from the linked primary mailbox. Other identities are for Gmail synchronization.</Typography.Text>
      {[['enabled', 'Enable signature'], ['apply_to_new_messages', 'Apply to new messages and forwards'], ['apply_to_replies', 'Apply to replies'], ['sync_to_gmail', 'Sync to Gmail']].map(([key, label]) => <label className="mail-signature-switch" key={key}><span>{label}</span><Switch aria-label={label} checked={form[key]} disabled={!editable || saving} onChange={value => change({ [key]: value })} /></label>)}
      <div className="mail-signature-switch"><strong>Signature</strong><Button size="small" onClick={() => setSource(value => !value)}>{source ? 'Visual editor' : 'Edit HTML'}</Button></div>
      {source ? <Input.TextArea aria-label="Signature HTML" value={form.signature_html} disabled={!editable || saving} rows={8} onChange={event => change({ signature_html: event.target.value })} /> : <MailEditor label="Signature" text={signatureText(form.signature_html)} html={form.signature_html} disabled={!editable || saving} onChange={value => change({ signature_html: value.html_body })} />}
      <strong>Preview</strong><div className="mail-signature-preview"><EmailHtmlViewer title="Signature preview" srcDoc={emailDocument(form.signature_html, true)} /></div>
      <div>Gmail sync: <Tag color={current?.gmail_sync_status === 'failed' ? 'warning' : current?.gmail_sync_status === 'synced' ? 'success' : 'default'}>{current?.gmail_sync_status || 'not_requested'}</Tag>{current?.gmail_last_synced_at && <small>Last synced {new Date(current.gmail_last_synced_at).toLocaleString()}</small>}</div>
      {current?.gmail_sync_status === 'failed' && <Alert type="warning" message="Saved in SRM; Gmail synchronization failed" description={current.gmail_last_error || 'Save again to retry Gmail synchronization.'} />}
      {editable && <Button type="primary" loading={saving} disabled={!form.send_as_email} onClick={save}>Save signature</Button>}
    </>}
  </div>;
}
